import type { TFunction } from 'i18next'

import type { components } from '#/api/schema'
import { createEnumGuard } from '#/utils/typeGuards'
import type { TStatusGroup } from '#lib/statusGroups'

/**
 * Derived from the generated spec rather than typed out again: the hand-written
 * list had drifted, carrying a 'replaced' the backend never sends and missing
 * 'ordering' and 'blocking', which therefore rendered without label or badge.
 */
export type TProductStatus = NonNullable<
  NonNullable<components['schemas']['MyPropositions']['personalCard']>['productStatus']
>

export const PRODUCT_STATUSES = [
  'ordering',
  'ordered',
  'in_production',
  'shipped',
  'active',
  'blocking',
  'blocked',
  'lost',
  'stolen',
  'ended',
  'photo_rejected',
] as const satisfies readonly TProductStatus[]

const isProductStatus = createEnumGuard(PRODUCT_STATUSES)

/** Unknown or missing product statuses become null. Callers that need a fallback decide it themselves. */
export function toProductStatus(value: string | null | undefined): TProductStatus | null {
  if (value === null || value === undefined) return null

  return isProductStatus(value) ? value : null
}

/**
 * Returns the translated label for a product status.
 * Uses static t() calls so i18next-parser can detect and extract the keys.
 */
export function getProductStatusLabel(status: TProductStatus, t: TFunction): string {
  switch (status) {
    case 'ordering':
      return t('Being ordered')
    case 'ordered':
      return t('Ordered')
    case 'in_production':
      return t('In production')
    case 'shipped':
      return t('Shipped')
    case 'active':
      return t('Active')
    case 'blocking':
      return t('Being blocked')
    case 'blocked':
      return t('Blocked')
    case 'lost':
      return t('Lost')
    case 'stolen':
      return t('Stolen')
    case 'ended':
      return t('Ended')
    case 'photo_rejected':
      return t('Photo rejected')
    default:
      return status
  }
}

export const PRODUCT_STATUS_VARIANT_MAP: Record<TProductStatus, TStatusGroup> = {
  active: 'success',
  blocking: 'warning',
  blocked: 'danger',
  lost: 'danger',
  stolen: 'danger',
  ended: 'neutral',
  ordering: 'info',
  ordered: 'info',
  in_production: 'info',
  shipped: 'info',
  // Not produced and not ended: the user has to supply a new passport photo (Foto afgekeurd).
  photo_rejected: 'danger',
}
